import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Camera, Download, FileUp, ClipboardPaste, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { parseTable, autoMap, validate } from '@/lib/planImport';
import { samplePlanTsv } from '@/data/planImportSample';
import { runAi } from '@/lib/ai';
import { fileToBase64, planExtractDemo, rowsToTsv } from '@/lib/planExtract';
import { AiBadge } from '@/components/ai/AiBadge';
import { ImportMapping } from './ImportMapping';
import { ImportPreview } from './ImportPreview';
import { HelpTip } from '@/components/ui/help-tip';

const MAPPING_KEY = 'omya_import_mapping';

// A saved mapping is reused when the same header row comes again.
const loadMapping = (headers) => {
  try { return JSON.parse(localStorage.getItem(MAPPING_KEY) || '{}')[headers.join('|')] || null; } catch { return null; }
};
const saveMapping = (headers, mapping) => {
  try {
    const all = JSON.parse(localStorage.getItem(MAPPING_KEY) || '{}');
    localStorage.setItem(MAPPING_KEY, JSON.stringify({ ...all, [headers.join('|')]: mapping }));
  } catch { /* ignore */ }
};

export const PlanImport = ({ loadings, onImport }) => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [mapping, setMapping] = useState({});
  const table = useMemo(() => parseTable(text), [text]);
  const existing = useMemo(() => new Set(loadings.map(l => l.orderId)), [loadings]);
  const rows = useMemo(() => validate(table.rows, mapping, existing), [table, mapping, existing]);

  const load = (value) => {
    setText(value);
    const { headers } = parseTable(value);
    setMapping(loadMapping(headers) || autoMap(headers));
  };

  // Photo or PDF of a printed plan → rows read by the AI → the same mapping and preview (#69).
  const [reading, setReading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const fromAi = (res) => {
    setExtracted(res);
    load(rowsToTsv(res.result.rows));
  };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 4_800_000) { toast.error(t('import.photo.tooLarge')); return; }
    setReading(true);
    fromAi(await runAi('plan-extract', await fileToBase64(file), planExtractDemo));
    setReading(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (/\.xlsx?$/i.test(file.name)) { toast.info(t('import.xlsxHint')); return; }
    load(await file.text());
  };

  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([samplePlanTsv().replace(/\t/g, ';')], { type: 'text/csv' }));
    Object.assign(document.createElement('a'), { href: url, download: 'plan-nakladek-ukazka.csv' }).click();
    URL.revokeObjectURL(url);
  };

  const doImport = (ok) => {
    saveMapping(table.headers, mapping);
    onImport(ok);
    toast.success(`${t('import.done')}: ${ok.length}`);
    navigate('/kamiony');
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 space-y-3 lg:col-span-2">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2"><ClipboardPaste className="h-4 w-4" /> {t('import.paste.title')} <HelpTip id="importPaste" /></div>
            <div className="text-xs text-muted-foreground">{t('import.paste.hint')}</div>
          </div>
          <Textarea
            aria-label={t('import.paste.title')}
            value={text}
            onChange={e => load(e.target.value)}
            placeholder={t('import.paste.placeholder')}
            className="font-mono text-xs h-32"
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="gap-1" asChild>
              <label className="cursor-pointer"><FileUp className="h-3.5 w-3.5" /> {t('import.file')}<input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" className="hidden" onChange={onFile} /></label>
            </Button>
            <Button variant="outline" size="sm" className="gap-1" onClick={() => load(samplePlanTsv())}>{t('import.useSample')}</Button>
            <Button variant="ghost" size="sm" className="gap-1" onClick={downloadSample}><Download className="h-3.5 w-3.5" /> {t('import.downloadSample')}</Button>
          </div>
        </Card>
        <Card className="p-4 space-y-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Camera className="h-4 w-4" /> {t('import.photo.title')} <HelpTip id="importPhoto" /></div>
          <div className="text-xs text-muted-foreground">{t('import.photo.hint')}</div>
          <Button variant="outline" size="sm" className="w-full gap-1" disabled={reading} asChild>
            <label className="cursor-pointer">
              {reading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} {t('import.photo.upload')}
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={onPhoto} aria-label={t('import.photo.upload')} />
            </label>
          </Button>
          <Button variant="ghost" size="sm" className="w-full" onClick={() => fromAi({ mode: 'demo', result: planExtractDemo() })}>
            {t('import.photo.demo')}
          </Button>
          {extracted && (
            <div className="flex items-start gap-2 text-xs text-muted-foreground" aria-live="polite">
              <AiBadge mode={extracted.mode} />
              <span>{t('import.photo.read').replace('{n}', extracted.result.rows.length)} {extracted.result.notes || (extracted.mode !== 'live' ? t('import.photo.demoNote') : '')}</span>
            </div>
          )}
        </Card>
      </div>

      {table.headers.length > 0 && (
        <>
          <ImportMapping headers={table.headers} mapping={mapping} onChange={setMapping} />
          <ImportPreview rows={rows} onImport={doImport} />
        </>
      )}
    </div>
  );
};
